/**
 * Single source of truth for quote line pricing and GST totals.
 * Inc-GST lines: sell_price is GST-inclusive. Ex-GST lines: sell_price is GST-free.
 */
import { isCalculatedQuoteTask, isTimeBasedQuoteTask } from './calculatedQuoteDisplay'
import { includesGstFlag } from './productPricing'

export function bankersRound (number, places = 0) {
  const x = number * Math.pow(10, places)
  const r = Math.round(x)
  const br = (((((x > 0) ? x : (-x)) % 1) === 0.5) ? (((0 === (r % 2))) ? r : (r - 1)) : r)
  return br / Math.pow(10, places)
}

function parseAmount (value) {
  if (value === null || value === undefined || value === '') return NaN
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''))
  return Number.isFinite(n) ? n : NaN
}

/** Subtotal for one quote line (before quote-level discount). */
export function quoteLineSubtotal (item) {
  if (!item) return 0
  const qty = Number(item.qty) || 0
  const sellPrice = Number(item.sell_price) || 0
  if (item.item_type === 'task') {
    const callout = Number(item.callout_fee) || 0
    const taskSubtotal = (isTimeBasedQuoteTask(item) || isCalculatedQuoteTask(item)) ? sellPrice : sellPrice * qty
    return taskSubtotal + callout * qty
  }
  if (item.item_type === 'product') {
    return sellPrice * qty
  }
  return 0
}

export function quoteItemIncludesGst (item) {
  if (!item) return true
  if (item.includes_gst != null) return includesGstFlag(item.includes_gst)
  if (item.item_details && item.item_details.includes_gst != null) {
    return includesGstFlag(item.item_details.includes_gst)
  }
  return true
}

export function resolveQuoteDiscount (itemsSubtotal, quote) {
  const q = quote || {}
  const applied = Number(q.discount_amount_applied)
  const override = parseFloat(q.discount_override)
  const contactSalesDiscount = q.contact && Number(q.contact.sales_discount)
  if (applied > 0) return applied
  if (override > 0) return override
  if (contactSalesDiscount > 0 && (q.discount_override === '' || q.discount_override == null)) {
    return (contactSalesDiscount / 100) * itemsSubtotal
  }
  return 0
}

/** Recompute quote totals from line items (sidebar, preview, save). */
export function calculateQuoteTotals (quote, items) {
  const quoteItems = Array.isArray(items) ? items : []
  let itemsSubtotal = 0
  let incGstSubtotal = 0
  quoteItems.forEach((it) => {
    const lineSub = quoteLineSubtotal(it)
    itemsSubtotal += lineSub
    if (quoteItemIncludesGst(it)) {
      incGstSubtotal += lineSub
    }
  })
  const quoteCallout = parseFloat(quote && quote.callout_fee)
  if (!isNaN(quoteCallout)) {
    itemsSubtotal += quoteCallout
    incGstSubtotal += quoteCallout
  }

  const discount = resolveQuoteDiscount(itemsSubtotal, quote)
  const subtotalAfterDiscount = Math.max(0, itemsSubtotal - discount)
  const discountRatio = itemsSubtotal > 0 ? subtotalAfterDiscount / itemsSubtotal : 0
  const incGstAfterDiscount = incGstSubtotal * discountRatio
  const totalIncGst = bankersRound(subtotalAfterDiscount, 2)
  const gst = bankersRound(incGstAfterDiscount / 11, 2)
  const subExGst = bankersRound(totalIncGst - gst, 2)

  return {
    discount: bankersRound(discount, 2),
    sub_total: subExGst,
    gst_total: gst,
    total: totalIncGst
  }
}

/** GST-inclusive total for list/search rows from stored quote fields, with optional line-item fallback. */
export function quoteDisplayTotal (quote, items) {
  if (!quote) return 0
  let total = parseAmount(quote.total)
  if (!Number.isFinite(total) || total <= 0) total = parseAmount(quote.total_amount)
  if (Number.isFinite(total) && total > 0) return bankersRound(total, 2)

  const quoteItems = Array.isArray(items) ? items
    : (Array.isArray(quote.items) ? quote.items : [])
  if (quoteItems.length > 0) {
    const normalizedItems = quoteItems.map((item) => {
      if (!item || item.sell_price != null) return item
      const unitPrice = parseAmount(item.unit_price)
      if (Number.isFinite(unitPrice)) return Object.assign({}, item, { sell_price: unitPrice })
      return item
    })
    const computed = calculateQuoteTotals(quote, normalizedItems).total
    if (Number.isFinite(computed) && computed > 0) return bankersRound(computed, 2)
  }

  return Number.isFinite(total) && total >= 0 ? bankersRound(total, 2) : 0
}

/** Map stored quote totals into sub_total / gst_total / total for preview modals. */
export function quoteTotalsFromStored (quote) {
  const total = quoteDisplayTotal(quote)
  return {
    sub_total: total,
    gst_total: bankersRound(total / 11, 2),
    total
  }
}

export function getQuoteLineUnitPrice (item) {
  const qty = Math.max(1, parseInt(item.qty, 10) || 0)
  const base = parseFloat(item.sell_price) || 0
  const callout = parseFloat(item.callout_fee) || 0
  if (item && item.item_type === 'task' && (isTimeBasedQuoteTask(item) || isCalculatedQuoteTask(item))) {
    return bankersRound(base / qty + callout, 2)
  }
  return base + callout
}

export function getQuoteLineTotal (item) {
  return bankersRound(quoteLineSubtotal(item), 2)
}
